import React, { useState } from "react";
import {
    Box,
    Typography,
    Divider,
    Checkbox,
    Button,
    CircularProgress,
    IconButton,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { FileUpload } from "./FileUpload";
import { AttachFile, Edit } from "@mui/icons-material";

interface CheckListProps {
    completedStatus: "completed" | "in_progress" | "not_started";
    title: string;
    owner: string;
    completedDate: string;
    downloadLink: string;
    isRequired: boolean;
    checkListId: string;
    isEvidenceRequired: boolean;
    onSave: () => Promise<void> | void;
    isLoading: boolean;
    onUploadComplete: (isEdit: boolean) => void;
    fileUploadRef?: React.RefObject<any>;
    checklistType?: "activity" | "stageGate";
}

export const CheckList: React.FC<CheckListProps> = ({
    completedStatus,
    completedDate,
    title,
    owner,
    checkListId,
    isRequired,
    isEvidenceRequired,
    onSave,
    isLoading = false,
    onUploadComplete,
    fileUploadRef,
    checklistType,
}) => {

    const isCompleted = completedStatus === "completed";

    const [status, setStatus] = useState(isCompleted);
    const [error, setError] = useState<string>("");

    const [editStatus, setEditStatus] = useState(false);

    const handleSave = async () => {
        await onSave();
        setEditStatus(false);
    };

    const handleCheckboxChange = () => {
        setStatus(!status);
    };

    const showFileUpload =
        status && (editStatus || !isCompleted);

    return (
        <Box
            borderColor={"divider"}
            borderRadius={1}
            px={1}
            py={2}
            sx={{ borderWidth: "1px", borderStyle: "solid" }}
            mb={2}
        >
            <Box display="flex" alignItems="start" width="100%" gap={1}>

                {/* checkbox */}
                <Checkbox
                    checked={status}
                    onChange={handleCheckboxChange}
                    disabled={(isCompleted || isLoading) && !editStatus}
                />

                {/* Title + owner */}
                <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="body2">{title}</Typography>

                    <Typography variant="caption" color="text.secondary">
                        {isCompleted
                            ? `Completed by ${owner} on ${completedDate}`
                            : `Owner: ${owner}`}
                    </Typography>

                    {/* File upload */}
                    { (
                        <>
                            <FileUpload
                                type="checklist"
                                ref={fileUploadRef}
                                typeId={checkListId}
                                isRequired={isEvidenceRequired}
                                isDisableEdit={!editStatus}
                                isDisableDelete={!editStatus}
                                onUploadComplete={() => onUploadComplete(editStatus)}
                            />
                            {error && (
                                <Typography variant="caption" color="error">
                                    {error}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                {/* Edit button (only if completed) */}
               {isCompleted && (
                    <IconButton
                        sx={{ color: "primary.main" }}
                        onClick={() => {
                            setEditStatus(true);
                            setStatus(true);
                        }}
                    >
                        {checklistType === "stageGate" ? <Edit /> : <AttachFile fontSize="small"/>}
                    </IconButton>
                )}
            </Box>

    
      {/* Save / Update button */}
            {status && (!isCompleted || editStatus) && (
                <Box display="flex" flexDirection="column" width="100%" gap={1}>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="flex-end" width="100%" gap={2}>
                        <Button onClick={() => {
                            setEditStatus(false);
                            setStatus(false);
                        }}>Cancel</Button>
                        <Button
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <CheckCircleOutlineIcon />
                                )
                            }
                            variant="contained"
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            {isLoading
                                ? "Saving..."
                                : editStatus
                                    ? "Update details"
                                    : "Save details"}
                        </Button>
                    </Box>
                </Box>
            )}

        </Box>
    );
};
